import { InjectionToken } from '@angular/core';
import { environment } from '../environments/environment-wrapper';
import { IAppConfig } from './app-config.interface';

export let APP_CONFIG = new InjectionToken<IAppConfig>('app.config');

export const APP_RECAPTCHA_KEY = '6Lcww1cUAAAAAH9eczzWwz8WhQkOJrQo5gF5lymj'; // replaced '6LceblAUAAAAAAVu-e7ajinJgMzARB-ksOCU8wr9';

export const MANAGE_NOTIFICATION_PATH = '/manage-notifications';
export const ESTIMATE_PATH = '/estimate';
export const MAX_LINKS_COUNT = '50';

export const GOOGLE_MAP_KEY = environment.googleMapKey;

export const GOOGLE_MAP_URL = 'https://www.google.com/maps';
export const GOOGLE_MAP_API_URL = 'https://maps.googleapis.com/maps/api/';

export const LISTING_PAGE_ICON_SRC = 'assets/icons/link-card/Listing_Icon.svg';
export const AGENT_PAGE_ICON_SRC = 'assets/icons/link-card/Agent_Icon.svg';
export const OPEN_HOUSE_PAGE_ICON_SRC =
  'assets/icons/link-card/OpenHouse_Icon.svg';

export const AgentPageSuffix = '-real-estate-agents';
export const OpenHousesSuffix = '-open-houses';
export const PopularCitySuffix = '-real-estate';
export const ListingDirectoryPageRoute = 'real-estate-listings-in-canada';
export const AgentDirectoryPageRoute = 'real-estate-agents-in-canada';
export const OpenHouseDirectoryPageRoute = 'open-houses-in-canada';
export const NeighbourhoodDirectoryPageRoute = 'neighbourhoods-in-canada';
export const RowTitleBoard = {
  lowercase: 'board',
  titlecase: 'Board'
};

export const APP_DI_CONFIG: IAppConfig = {
  urls: {
    office: {
      base: 'api/v1/office/',
      listings: 'api/v1/office/listings/',
      agents: 'api/v1/office/agents/',
      search: 'api/v1/office/search/',
      searchByDistance: 'api/v1/office/searchByDistance'
    },
    agent: {
      base: 'api/v1/agent/',
      listings: 'api/v1/agent/listings/',
      search: 'api/v1/agent/search/',
      filters: 'api/v1/agent/filters',
      count: 'api/v1/agent/count/',
      config: 'api/v1/agent/config/'
    },
    enhancedContent: {
      base: 'api/v1/enhancedcontent/'
    },
    listings: {
      base: 'api/v1/listings/',
      active: 'api/v1/listings/active',
      detail: 'api/v1/listings/detail/',
      count: 'api/v1/listings/count',
      provinceCity: 'api/v1/listings/ca', // ex: ca/on/toronto
      preview: 'api/v1/listings/previews/',
      gallery: 'api/v1/listings/gallery/',
      search: 'api/v1/listings/search/',
      distance: 'api/v1/listings/distance',
      filters: 'api/v1/listings/filters',
      sitemap: 'api/v1/listings/recentListings',
      offmarket: 'api/v1/properties/',
      addPageView: 'api/v1/listings/addPageView'
    },
    poi: {
      base: 'api/v1/poi'
    },
    schools: {
      base: 'api/v1/schools',
      preview: 'api/v1/schools/preview/'
    },
    user: {
      base: 'api/v1/user',
      login: 'api/v1/user/login',
      socialLogin: 'api/v1/user/login/social',
      logout: 'api/v1/user/logout',
      accounts: 'api/v1/user/accounts',
      checkPasswordToken: 'api/v1/user/password/check-reset-link/',
      resetPassword: 'api/v1/user/password/reset',
      changePassword: 'api/v1/user/password/change',
      forgotPassword: 'api/v1/user/password/forgot',
      renewToken: 'api/v1/user/renew',
      restoreSession: 'api/v1/user/restoreSession',
      retrieveSavedSearch: 'api/v1/user/savedSearches',
      unsubscribeSavedSearchNotification: 'api/v1/user/unsubscribe'
    },
    mortgage: {
      base: 'api/v1/mortgage'
    },
    leads: {
      base: 'api/v1/leads',
      share: 'api/v1/leads/share'
    },
    blog: {
      base: 'api/v1/blog'
    },
    internalLinks: {
      base: 'api/v1/internalLinks',
      directory: 'api/v1/internalLinks/directory',
      neighborhood: 'api/v1/internalLinks/neighbourhoods'
    },
    homeEstimator: {
      base: 'api/v1/homeEstimation/bc',
      estimate: 'api/v1/homeEstimation/bc/estimate',
      suggestions: 'api/v1/homeEstimation/bc/findByAddress',
      offmarket: 'api/v1/properties/'
    },
    geo: {
      base: 'api/v1/geo',
      geocode: 'api/v1/geo/geocode'
    },
    demographics: {
      base: 'api/v1/demographics',
      point: 'api/v2/demographics/point',
      neighbourhoodPoint: 'api/v1/demographics/neighbourhood/point',
      neighbourhoodId: 'api/v2/demographics/neighbourhood/id'
    }
  }
};

// Values are translate tags except for url
export const DIRECTORY_STRUCTURE_CONFIG = {
  openHouse: {
    title: 'directory-page.open-houses',
    sublink1: {
      title: 'directory-page.real-estate',
      url: `/${ListingDirectoryPageRoute}`
    },
    sublink2: {
      title: 'directory-page.real-estate-agents',
      url: `/${AgentDirectoryPageRoute}`
    },
    sublink3: {
      title: 'directory-page.neighbourhoods',
      url: `/${NeighbourhoodDirectoryPageRoute}`
    },
    cardStructure: {
      main: {
        title: 'directory-page.open-houses',
        urlSuffix: OpenHousesSuffix
      },
      sub1: {
        title: 'directory-page.listings',
        urlSuffix: PopularCitySuffix,
        icon: LISTING_PAGE_ICON_SRC
      },
      sub2: {
        title: 'directory-page.agents',
        urlSuffix: AgentPageSuffix,
        icon: AGENT_PAGE_ICON_SRC
      }
    }
  },
  listing: {
    title: 'directory-page.real-estate',
    sublink1: {
      title: 'directory-page.open-houses',
      url: `/${OpenHouseDirectoryPageRoute}`
    },
    sublink2: {
      title: 'directory-page.real-estate-agents',
      url: `/${AgentDirectoryPageRoute}`
    },
    sublink3: {
      title: 'directory-page.neighbourhoods',
      url: `/${NeighbourhoodDirectoryPageRoute}`
    },
    cardStructure: {
      main: {
        title: 'directory-page.listings',
        urlSuffix: PopularCitySuffix
      },
      sub1: {
        title: 'directory-page.open-houses',
        urlSuffix: OpenHousesSuffix,
        icon: OPEN_HOUSE_PAGE_ICON_SRC
      },
      sub2: {
        title: 'directory-page.agents',
        urlSuffix: AgentPageSuffix,
        icon: AGENT_PAGE_ICON_SRC
      }
    }
  },
  agent: {
    title: 'directory-page.real-estate-agents',
    sublink1: {
      title: 'directory-page.real-estate',
      url: `/${ListingDirectoryPageRoute}`
    },
    sublink2: {
      title: 'directory-page.open-houses',
      url: `/${OpenHouseDirectoryPageRoute}`
    },
    sublink3: {
      title: 'directory-page.neighbourhoods',
      url: `/${NeighbourhoodDirectoryPageRoute}`
    },
    cardStructure: {
      main: {
        title: 'directory-page.agents',
        urlSuffix: AgentPageSuffix
      },
      sub1: {
        title: 'directory-page.listings',
        urlSuffix: PopularCitySuffix,
        icon: LISTING_PAGE_ICON_SRC
      },
      sub2: {
        title: 'directory-page.open-houses',
        urlSuffix: OpenHousesSuffix,
        icon: OPEN_HOUSE_PAGE_ICON_SRC
      }
    }
  },
  neighborhood: {
    title: 'directory-page.neighbourhoods',
    sublink1: {
      title: 'directory-page.real-estate',
      url: `/${ListingDirectoryPageRoute}`
    },
    sublink2: {
      title: 'directory-page.real-estate-agents',
      url: `/${AgentDirectoryPageRoute}`
    },
    sublink3: {
      title: 'directory-page.open-houses',
      url: `/${OpenHouseDirectoryPageRoute}`
    }
  }
};

export const Disclaimer = {
  crea: {
    disclaimer:
      'This listing content provided by REALTOR.ca <a href="https://www.realtor.ca/" target="_blank" rel="nofollow noopener" >https://www.realtor.ca/</a> has been licensed by REALTOR® members of The Canadian Real Estate Association <a href="https://www.crea.ca/" rel="nofollow noopener" target="_blank">https://www.crea.ca/</a>',
    disclosure:
      'The trademarks REALTOR®, REALTORS®, and the REALTOR® logo are controlled by The Canadian Real Estate Association (CREA) and identify real estate professionals who are members of CREA. The trademarks MLS®, Multiple Listing Service® and the associated logos are owned by The Canadian Real Estate Association (CREA) and identify the quality of services provided by real estate professionals who are members of CREA.'
  }
};

export const victoriaRealEstate = 'victoria real estate';
export const victoriaRealEstateIdxLink = 'https://vreb.org/vreb-idx-reciprocity-program';
export const OAAgentProvider = 'iFranchise-OA';
export const caraBoardPartialName = 'central alberta';
export const Luxury = 'luxury';
export const Commercial = 'commercial';
export const IndividualAgentAwards = 'agent-profile.awards.individual';
export const BritishColumbiaStateShort = 'BC';
export const DisableSellersLeadFormStateShorts = [];
export const CreaBoardName = '63144';
export const DisableAgentForLsids = [
  '731',
  '733',
  '734',
  '796',
  '3116',
  '63144'
];
export const QuebecStateShort = 'QC';
export const EdmontonBoard = 'edmonton real estate board';
export const EdmontonSource = '732';
export const AllowedHveCities = []; // Used for turning off login for city level offmarket estimates
// Used for courtesy pipe for non remax agents to display office name only
//
export const DisableAgentForBoards = [
  'chilliwack & district real estate board',
  'fraser valley real estate board',
  'real estate board of greater vancouver',
  'south okanagan real estate board',
  'vancouver island real estate board',
  'crea'
];
export const CottageSEOOverrides = [['on', ['muskoka', 'haliburton']]];

export const OARegions = ['on', 'nb', 'pei', 'pe', 'nl', 'ns'];

export const Provinces = [
  { short: 'ON', medium: 'ont', long: 'Ontario' },
  { short: 'NB', medium: '', long: 'New Brunswick' },
  { short: 'PEI', medium: '', long: 'Prince Edward Island' },
  { short: 'PE', medium: '', long: 'Prince Edward Island' },
  { short: 'NL', medium: '', long: 'Newfoundland and Labrador' },
  { short: 'NS', medium: '', long: 'Nova Scotia' },
  { short: 'BC', medium: '', long: 'British Columbia' },
  { short: 'AB', medium: '', long: 'Alberta' },
  { short: 'MB', medium: '', long: 'Manitoba' },
  { short: 'YT', medium: '', long: 'Yukon' },
  { short: 'NU', medium: '', long: 'Nunavut' },
  { short: 'QC', medium: '', long: 'Quebec' },
  { short: 'SK', medium: '', long: 'Saskatchewan' }
];

export const LandingPagePopularCities = [
  {
    name: 'Vancouver, BC',
    url: '/bc/vancouver-real-estate',
  },
  {
    name: 'Toronto, ON',
    url: '/on/toronto-real-estate',
  },
  {
    name: 'Ottawa, ON',
    url: '/on/ottawa-real-estate',
  },
  {
    name: 'Halifax, NS',
    url: '/ns/halifax-real-estate',
  },
  {
    name: 'Saskatoon, SK',
    url: '/sk/saskatoon-real-estate',
  },
  {
    name: 'Edmonton, AB',
    url: '/ab/edmonton-real-estate',
  },
  {
    name: 'Calgary, AB',
    url: '/ab/calgary-real-estate',
  },
  {
    name: 'Winnipeg, MB',
    url: '/mb/winnipeg-real-estate',
  },
  {
    name: 'Hamilton, ON',
    url: '/on/hamilton-real-estate',
  },
  {
    name: 'Surrey, BC',
    url: '/bc/surrey-real-estate',
  }
];

// Used for META DESCRIPTION creation, replacing abbreviations with long form
export const StreetTypes = {
  "AVE": "Avenue",
  "BVLD": "Boulevard",
  "C": "Centre",
  "CIR": "Circle",
  "CRES": "Crescent",
  "CRT": "Court",
  "DR": "Drive",
  "ESTATE": "Estates",
  "GRNDS": "Grounds",
  "HARBR": "Harbour",
  "HGHLDS": "Highlands",
  "LANDNG": "Landing",
  "PK": "Park",
  "PKY": "Parkway",
  "PL": "Place",
  "PLAT": "Plateau",
  "RD": "Road",
  "RG": "Range",
  "SQ": "Square",
  "ST": "Street",
  "TERR": "Terrace",
  "VILLGE": "Village"
};
